import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });

/**
 * Counselor-facing card: "FuturePath Change Detected".
 *
 * Lists every student on the caseload whose recent activity suggests their
 * CDP direction is evolving. Counselor clicks to see the emerging direction
 * + signal evidence + jump into that student's snapshot.
 */
export default function CDPEvolutionAlerts({ onOpenStudent }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/nc/cdp/counselor/alerts`, auth())
      .then((r) => setAlerts(r.data.alerts || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || alerts.length === 0) return null;

  const unresolved = alerts.filter((a) => !a.response);
  const considering = alerts.filter((a) => a.response === 'considering');
  const updates = alerts.filter((a) => a.response === 'update');

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div
      data-testid="cdp-evolution-alerts"
      className="relative overflow-hidden rounded-2xl border border-violet-400/30 bg-[#0a1729]/60"
      style={{ boxShadow: '0 30px 80px -60px rgba(167,139,250,0.55)' }}
    >
      <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full blur-3xl opacity-30"
        style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.4), transparent 65%)' }} />

      <div className="relative">
        <div className="flex items-center gap-2.5 border-b border-white/[0.07] px-6 py-4">
          <Sparkles className="h-4 w-4 text-violet-300" />
          <h2 className="font-outfit text-base font-semibold text-white">FuturePath Change Detected</h2>
          <span className="ml-2 rounded-full border border-violet-300/40 bg-violet-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-violet-200">
            {alerts.length} student{alerts.length === 1 ? '' : 's'}
          </span>
          <span className="ml-auto text-[11px] text-slate-300">
            {unresolved.length} unresolved · {considering.length} considering · {updates.length} confirmed
          </span>
        </div>

        <div className="divide-y divide-white/[0.05]">
          {alerts.map((a) => {
            const isOpen = expanded[a.student_id];
            const strengthHue =
              a.strength === 'strong' ? '#f472b6' :
              a.strength === 'considering' ? '#a78bfa' : '#38bdf8';
            const responseLabel =
              a.response === 'update' ? 'Direction updated'
              : a.response === 'considering' ? 'Considering, talk to student'
              : 'Not yet responded';
            const responseHue =
              a.response === 'update' ? '#22d3ee'
              : a.response === 'considering' ? '#a78bfa' : '#fbbf24';

            return (
              <div key={a.student_id} data-testid={`evolution-alert-${a.student_id}`}>
                <button
                  onClick={() => toggle(a.student_id)}
                  className="flex w-full items-center gap-4 px-6 py-4 text-left hover:bg-white/[0.04] transition-colors"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border text-base font-semibold text-white"
                    style={{ background: `${strengthHue}18`, borderColor: `${strengthHue}55` }}>
                    {(a.student_name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">
                      {a.student_name} <span className="text-slate-300 font-normal">· Grade {a.grade || '-'}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-slate-300 truncate">
                      <span className="text-slate-300">{a.current_career || 'Undecided'}</span>
                      <span className="mx-1.5 text-slate-300">→</span>
                      <span className="text-white">{a.emerging_career}</span>
                    </p>
                  </div>
                  <span className="hidden md:inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider"
                    style={{ borderColor: `${strengthHue}55`, background: `${strengthHue}18`, color: strengthHue }}>
                    {a.strength}
                  </span>
                  <span className="hidden md:inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider"
                    style={{ borderColor: `${responseHue}55`, background: `${responseHue}18`, color: responseHue }}>
                    {responseLabel}
                  </span>
                  {isOpen ? <ChevronUp className="h-4 w-4 text-slate-300" /> : <ChevronDown className="h-4 w-4 text-slate-300" />}
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-white/[0.04] px-6 py-4 bg-slate-900/40 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="rounded-xl border border-slate-600/40 bg-slate-900/50 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-slate-300">Current CDP</p>
                            <p className="mt-1 text-sm font-semibold text-white">{a.current_career || 'Undecided'}</p>
                            <p className="mt-0.5 text-[11px] text-slate-300">{a.current_cluster || '-'}</p>
                          </div>
                          <div className="rounded-xl border border-violet-400/40 bg-violet-500/10 p-3"
                            style={{ boxShadow: '0 0 22px -12px rgba(167,139,250,0.6)' }}>
                            <p className="text-[10px] uppercase tracking-wider text-violet-200">Emerging direction</p>
                            <p className="mt-1 text-sm font-semibold text-white">{a.emerging_career}</p>
                            <p className="mt-0.5 text-[11px] text-violet-200/80">{a.emerging_cluster}</p>
                          </div>
                        </div>

                        {a.signals?.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-300 mb-1.5">Why FuturePath noticed</p>
                            <p className="text-sm text-slate-200 leading-relaxed">
                              Recent activity shows increased interest in{' '}
                              <span className="text-white">{a.signals.join(', ')}</span>.
                            </p>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => onOpenStudent && onOpenStudent(a.student_id)}
                            data-testid={`evolution-open-student-${a.student_id}`}
                            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95 transition-opacity"
                            style={{ boxShadow: '0 0 22px -8px rgba(34,211,238,0.6)' }}
                          >
                            Open student <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                          <span className="text-[11px] text-slate-300 self-center">
                            Detected {a.detected_at ? new Date(a.detected_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
